/**
 * FortifyTrailLayer — renders a dashed teal trail drawing from the source
 * territory to the destination. No troop-change label is drawn.
 *
 * Animation timeline (full motion):
 *   0–400ms   Trail draws from source to target via strokeDashoffset
 *   400–650ms Trail holds
 *   650–950ms Trail fades out
 *
 * Trail colour: #0891B2 (teal) at 70% opacity; white shadow beneath for
 * legibility against dark territory fills.
 *
 * Reduced motion: a static arrow, with 100ms fade-in, 600ms hold,
 * 100ms fade-out. No drawing motion.
 *
 * Placed ABOVE AttackBadgeLayer in the layer stack.
 * pointerEvents="none" / accessibilityElementsHidden throughout.
 */

import React from 'react'
import { Platform } from 'react-native'
import { Line, G, Polygon } from 'react-native-svg'
import {
  useSharedValue,
  useAnimatedProps,
  withSequence,
  withTiming,
  Easing,
  createAnimatedComponent,
} from 'react-native-reanimated'
import { useReducedMotion } from 'react-native-reanimated'
import { fortifyTrailKey, type FortifyAnimationEntry } from '../../hooks/useFortifyEvents'
import { computeArrowGeometry, sourceArrowInset, targetArrowInset } from '../../lib/arrowGeometry'
import { DirectionArrow } from './DirectionArrow'

const AnimatedGroup = createAnimatedComponent(G)
const AnimatedLine = createAnimatedComponent(Line)
const AnimatedPolygon = createAnimatedComponent(Polygon)

// ─── Individual fortify animation ─────────────────────────────────────────────

interface FortifyAnimProps {
  fromX: number
  fromY: number
  /** Centre of the destination troop-count badge (badgePositionById, PoI-corrected). */
  toBadgeX: number
  toBadgeY: number
  /** Radius of the destination badge circle in viewBox units (MapView's badgeRadius). */
  toBadgeRadius: number
  trailWidth: number
  reducedMotion: boolean
}

function FortifyAnim({
  fromX,
  fromY,
  toBadgeX,
  toBadgeY,
  toBadgeRadius,
  trailWidth,
  reducedMotion,
}: FortifyAnimProps) {
  const arrowSize = trailWidth * 4
  // Compute the arrow geometry using the pure helper.
  // shaftTo is the base of the arrowhead (where the line terminates). Both
  // ends sit just inside the badge circles, clear of the digits (see
  // source/targetArrowInset); short hops shrink the gap and head so a tail
  // always shows.
  const arrow = computeArrowGeometry(
    { x: fromX, y: fromY },
    { x: toBadgeX, y: toBadgeY },
    targetArrowInset(toBadgeRadius),
    arrowSize,
    sourceArrowInset(toBadgeRadius),
  )

  // Total path length for the dash trick — measure from the shaft start to
  // the SHAFT end so the animated dash drives from source to the arrowhead base.
  const pathLen = Math.hypot(arrow.shaftTo.x - arrow.shaftFrom.x, arrow.shaftTo.y - arrow.shaftFrom.y)
  // Each dash equals the total path length so we get one "dash" that
  // represents the entire line. Animating dashOffset from pathLen → 0
  // draws it in.
  const dashLen = pathLen

  const trailDashOffset = useSharedValue(pathLen)
  const trailOpacity = useSharedValue(reducedMotion ? 0 : 0.7)
  const reducedOpacity = useSharedValue(0)

  React.useEffect(() => {
    if (reducedMotion) {
      // Preserve the directional cue without drawing/moving it.
      reducedOpacity.value = withSequence(
        withTiming(1, { duration: 100 }),
        withTiming(1, { duration: 600 }),
        withTiming(0, { duration: 100 }),
      )
      return
    }

    // Trail draws in over 400ms (was 800ms — snappier without losing clarity)
    trailDashOffset.value = withTiming(0, {
      duration: 400,
      easing: Easing.inOut(Easing.quad),
    })

    // Trail stays at 0.7 opacity for draw/hold, then fades.
    trailOpacity.value = withSequence(
      withTiming(0.7, { duration: 650 }),
      withTiming(0, { duration: 300, easing: Easing.in(Easing.quad) }),
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const trailAnimatedProps = useAnimatedProps(() => ({
    strokeDashoffset: trailDashOffset.value,
    strokeOpacity: trailOpacity.value,
  }))

  const shadowAnimatedProps = useAnimatedProps(() => ({
    strokeDashoffset: trailDashOffset.value,
    // Shadow opacity tracks the trail opacity but at 40%
    strokeOpacity: trailOpacity.value * (0.4 / 0.7),
  }))

  const reducedAnimatedProps = useAnimatedProps(() => ({ opacity: reducedOpacity.value }))
  // Arrowhead rides the trail's opacity track so it sits on the line
  // rather than appearing before the line has been drawn.
  const arrowAnimatedProps = useAnimatedProps(() => ({
    opacity: trailOpacity.value,
  }))

  return (
    <G
      testID="fortify-result-arrow"
      pointerEvents="none"
      {...(Platform.OS === 'ios' && { accessibilityElementsHidden: true })}
      {...(Platform.OS === 'android' && { importantForAccessibility: 'no-hide-descendants' as const })}
      {...(Platform.OS === 'web' && ({ 'aria-hidden': true } as { 'aria-hidden': true }))}
    >
      {/* Trail shadow underneath — improves legibility over dark fills.
          The line runs from source to the arrowhead BASE (shaftTo), not
          all the way to the badge centre, so the stroke doesn't poke
          through the arrowhead polygon. */}
      {!reducedMotion && (
        <AnimatedLine
          x1={arrow.shaftFrom.x}
          y1={arrow.shaftFrom.y}
          x2={arrow.shaftTo.x}
          y2={arrow.shaftTo.y}
          stroke="#ffffff"
          strokeWidth={trailWidth * 1.8}
          strokeDasharray={`${dashLen} ${dashLen}`}
          strokeLinecap="round"
          animatedProps={shadowAnimatedProps}
          pointerEvents="none"
        />
      )}
      {/* Teal trail */}
      {!reducedMotion && (
        <AnimatedLine
          x1={arrow.shaftFrom.x}
          y1={arrow.shaftFrom.y}
          x2={arrow.shaftTo.x}
          y2={arrow.shaftTo.y}
          stroke="#0891B2"
          strokeWidth={trailWidth}
          strokeDasharray={`${dashLen} ${dashLen}`}
          strokeLinecap="round"
          animatedProps={trailAnimatedProps}
          pointerEvents="none"
        />
      )}
      {/* Directional arrowhead — tip lands exactly on the badge
          circumference (pull-back = badgeRadius, not font size).
          Fades in with the trail's opacity track so it doesn't pop
          on top of an undrawn line. */}
      {!reducedMotion && (
        <AnimatedPolygon
          points={arrow.arrowPoints}
          fill="#0891B2"
          stroke="#ffffff"
          strokeWidth={trailWidth * 0.6}
          strokeLinejoin="round"
          animatedProps={arrowAnimatedProps}
          pointerEvents="none"
        />
      )}
      {reducedMotion && (
        <AnimatedGroup animatedProps={reducedAnimatedProps}>
          <DirectionArrow from={{ x: fromX, y: fromY }} to={{ x: toBadgeX, y: toBadgeY }}
            badgeRadius={toBadgeRadius} trailWidth={trailWidth} />
        </AnimatedGroup>
      )}
    </G>
  )
}

// ─── Static (snapshot) variant ────────────────────────────────────────────────

interface StaticFortifyProps {
  fromX: number
  fromY: number
  /** Centre of the destination troop-count badge (badgePositionById, PoI-corrected). */
  toBadgeX: number
  toBadgeY: number
  /** Radius of the destination badge circle in viewBox units (MapView's badgeRadius). */
  toBadgeRadius: number
  trailWidth: number
}

/**
 * Non-animated fortify trail for time-machine / snapshot mode.
 * Shares the visual treatment of FortifyAnim but holds at full
 * opacity and skips the draw-in animation, so the user sees the
 * exact line + arrow they'd see at the live animation's
 * peak.
 */
function StaticFortify({
  fromX,
  fromY,
  toBadgeX,
  toBadgeY,
  toBadgeRadius,
  trailWidth,
}: StaticFortifyProps) {
  return (
    <G
      testID="fortify-result-arrow"
      pointerEvents="none"
      {...(Platform.OS === 'ios' && { accessibilityElementsHidden: true })}
      {...(Platform.OS === 'android' && { importantForAccessibility: 'no-hide-descendants' as const })}
      {...(Platform.OS === 'web' && ({ 'aria-hidden': true } as { 'aria-hidden': true }))}
    >
      <DirectionArrow from={{ x: fromX, y: fromY }} to={{ x: toBadgeX, y: toBadgeY }}
        badgeRadius={toBadgeRadius} trailWidth={trailWidth} />
    </G>
  )
}

// ─── Layer ────────────────────────────────────────────────────────────────────

interface FortifyTrailLayerProps {
  entries: FortifyAnimationEntry[]
  /**
   * Blueprint centroid lookup (territory.position) — FALLBACK ONLY for
   * territories missing a badge position. Both ends of the arrow prefer
   * badgePositionById: a badge moved via a badgeAnchor override must move
   * the trail's start with it, or the arrow animates from the stale
   * centroid while the troop-count marker sits somewhere else.
   */
  positionById: Record<string, { x: number; y: number } | undefined>
  /**
   * PoI-corrected badge position lookup (badgePositionById from MapView).
   * Used for the DESTINATION badge centre so the arrowhead tip lands
   * exactly on the outer edge of the rendered troop-count badge.
   */
  badgePositionById: Record<string, { x: number; y: number } | undefined>
  /** Badge circle radius in viewBox units (MapView's badgeRadius). */
  badgeRadius: number
  /** Trail stroke width in SVG viewBox units (pass MapView's validStrokeWidth). */
  trailWidth: number
  /**
   * Frozen fortify entry for time-machine / snapshot view. When set,
   * one non-animated trail + arrow is rendered alongside any
   * live entries. Independent of the live `entries`-driven animation;
   * the live layer can be empty and this still draws.
   */
  staticEntry?: FortifyAnimationEntry
}

export function FortifyTrailLayer({
  entries,
  positionById,
  badgePositionById,
  badgeRadius,
  trailWidth,
  staticEntry,
}: FortifyTrailLayerProps) {
  const reducedMotion = useReducedMotion()

  if (entries.length === 0 && !staticEntry) return null

  const staticPositions =
    staticEntry &&
    (() => {
      // PoI-corrected badge positions for BOTH ends (centroid fallback), so
      // the frozen trail matches where the badges actually render.
      const from =
        badgePositionById[staticEntry.fromTerritoryId] ??
        positionById[staticEntry.fromTerritoryId]
      const toBadge =
        badgePositionById[staticEntry.toTerritoryId] ??
        positionById[staticEntry.toTerritoryId]
      return from && toBadge ? { from, toBadge } : null
    })()

  return (
    <>
      {entries.map((entry) => {
        // PoI-corrected badge positions for BOTH ends (centroid fallback):
        // the trail must start under the source troop-count badge — which a
        // badgeAnchor override may have moved off the centroid — and the
        // arrowhead tip must land on the destination badge circumference.
        const fromPos =
          badgePositionById[entry.fromTerritoryId] ?? positionById[entry.fromTerritoryId]
        const toBadgePos =
          badgePositionById[entry.toTerritoryId] ?? positionById[entry.toTerritoryId]
        if (!fromPos || !toBadgePos) return null
        return (
          <FortifyAnim
            key={fortifyTrailKey(entry)}
            fromX={fromPos.x}
            fromY={fromPos.y}
            toBadgeX={toBadgePos.x}
            toBadgeY={toBadgePos.y}
            toBadgeRadius={badgeRadius}
            trailWidth={trailWidth}
            reducedMotion={reducedMotion}
          />
        )
      })}
      {staticEntry && staticPositions && (
        <StaticFortify
          fromX={staticPositions.from.x}
          fromY={staticPositions.from.y}
          toBadgeX={staticPositions.toBadge.x}
          toBadgeY={staticPositions.toBadge.y}
          toBadgeRadius={badgeRadius}
          trailWidth={trailWidth}
        />
      )}
    </>
  )
}
